import { rank, route, type Finding } from '@/shared/ui/council'
import type { Assay } from '../api'

export type { Finding }

/**
 * The dossier council — what this record rests on, read as decisions.
 *
 * ## The one question a file has to answer
 *
 * Not "how much do we have" — a file of four hundred weak items is worse than a file of
 * twenty strong ones, because it invites confidence it cannot support. The question is
 * whether anything in here would survive being challenged, and the findings are ordered by
 * how much of the record is at risk of not surviving.
 *
 * ## Three claims this council refuses to make
 *
 * **Unverified is not false.** It means nobody has corroborated it yet. A council that
 * called unverified material doubtful would quietly delete real intelligence.
 *
 * **Undated is not instant.** An item with no event date has no latency, and coalescing
 * that to zero would report "we found out immediately" about something nobody has dated.
 * It is its own category everywhere here.
 *
 * **A single source is not a lie.** It is an unrepeated observation, which is a different
 * and narrower statement — and the correct response is to go and look for a second one, not
 * to discard the first.
 */
export function advise(a: Assay): Finding[] {
  const out: Finding[] = []
  const s = a.standing
  const num = (n: number | null | undefined) =>
    n == null ? '—' : Math.round(n).toLocaleString()
  const pct = (n: number) => `${Math.round(n * 100)}%`
  const share = (n: number) => (s.items > 0 ? n / s.items : 0)
  const hours = (m: number) => (m < 60 ? `${Math.round(m)} min`
    : m < 60 * 48 ? `${(m / 60).toFixed(1)} hours` : `${Math.round(m / 1440)} days`)

  // ── would any of this survive a challenge? ─────────────────────────────────
  if (s.items > 0) {
    const solid = share(s.atOrAboveFloor)
    out.push({
      id: 'standing',
      severity: solid < 0.4 ? 'critical' : solid < 0.7 ? 'serious' : 'good',
      headline: solid >= 0.7
        ? `${pct(solid)} of this record is strong enough to use.`
        : `Only ${pct(solid)} of this record is strong enough to use.`,
      evidence: `${num(s.atOrAboveFloor)} of ${num(s.items)} items meet the quality floor`
        + ` (${a.floor.reliability}${a.floor.credibility}); ${num(s.belowFloor)} fall short of it.`,
      action: solid >= 0.7
        ? 'You can build a public argument on this file. Quote from the items that meet the floor and leave the rest as background — mixing them is how a strong case gets discredited by its weakest line.'
        : 'Do not build a public argument on this file yet. Most of what is in it would not survive being challenged, and one weak item quoted in public discredits the strong ones beside it. Send collectors after corroboration before anything goes out.',
      votesAtStake: s.belowFloor,
    })
  }

  // ── what only one publication is telling us ───────────────────────────────
  if (s.singleSource > 0) {
    const sh = share(s.singleSource)
    out.push({
      id: 'single-source',
      severity: sh > 0.5 ? 'serious' : 'watch',
      headline: `${pct(sh)} of the record rests on a single source.`,
      evidence: `${num(s.singleSource)} items are uncorroborated; ${num(s.corroborated)} have`
        + ` a second source. ${num(s.singleSourceSubjects)} whole files come from one publication.`,
      action: 'A single source is not a lie — it is an unrepeated observation, and the fix is to go and find the second one, not to throw away the first. Start with the single-sourced items that matter most to the argument you actually intend to make.',
      votesAtStake: s.singleSource,
    })
  }

  // ── what somebody is already fighting about ───────────────────────────────
  if (s.disputed > 0 || s.debunked > 0) {
    out.push({
      id: 'contested',
      severity: s.debunked > 0 ? 'serious' : 'watch',
      headline: s.debunked > 0
        ? `${num(s.debunked)} items in this file have been debunked, and ${num(s.disputed)} are disputed.`
        : `${num(s.disputed)} items are disputed.`,
      evidence: `Out of ${num(s.items)} items in the window.`,
      action: s.debunked > 0
        ? 'Debunked material must be marked and never quoted — but do NOT delete it. A file that quietly drops what turned out to be wrong cannot show that it corrected itself, and that is the first thing anybody auditing this will look for.'
        : 'Disputed means somebody has contested it, not that it is wrong. Find out who disputed it and on what basis before deciding whether it can be used.',
      votesAtStake: s.disputed + s.debunked,
    })
  }

  // ── how stale the record is by the time we hold it ────────────────────────
  if (s.latencyP50Minutes > 0) {
    const slow = s.latencyP90Minutes > 60 * 24 * 30
    out.push({
      id: 'latency',
      severity: slow ? 'serious' : 'watch',
      headline: `Half of what this file holds reached us ${hours(s.latencyP50Minutes)} after it happened.`,
      evidence: `The slowest tenth took over ${hours(s.latencyP90Minutes)}.`
        + (s.undated > 0 ? ` A further ${num(s.undated)} items carry no event date at all.` : ''),
      action: slow
        ? 'A record this far behind events is a history, not an early-warning system. If you need to react to things rather than describe them afterwards, the collection plan needs a faster source — not more of the same one.'
        : 'This is fast enough to act on. Keep the sources that are delivering it; the ones in the slow tail are worth reviewing against what they cost.',
      votesAtStake: 0,
    })
  }

  if (s.undated > 0 && share(s.undated) > 0.15) {
    out.push({
      id: 'undated',
      severity: 'watch',
      headline: `${pct(share(s.undated))} of items have no date for when the event happened.`,
      evidence: `${num(s.undated)} of ${num(s.items)} items.`,
      action: 'Undated is not "instant" — it means nobody recorded when it happened, so these items cannot be placed in a sequence or used to show that one thing followed another. If the argument depends on chronology, these cannot carry it.',
      votesAtStake: s.undated,
    })
  }

  // ── who is telling us everything ──────────────────────────────────────────
  if (a.sources.length > 0 && s.items > 0) {
    const top = [...a.sources].sort((x, y) => y.items - x.items)[0]!
    if (top.items / s.items > 0.35) {
      out.push({
        id: 'source-concentration',
        severity: top.items / s.items > 0.6 ? 'serious' : 'watch',
        headline: `${pct(top.items / s.items)} of everything in this file comes from ${top.name}.`,
        evidence: a.sources.slice(0, 4)
          .map((x) => `${x.name} ${num(x.items)} (${num(x.atOrAboveFloor)} strong)`).join(' · '),
        action: 'One source carrying the file means the file describes what that source chose to cover, and its blind spots are invisible from inside this page. It also means one retraction can move most of your record at once.',
        votesAtStake: top.items,
        cellCodes: a.sources.slice(0, 5).map((x) => x.name),
      })
    }

    const weak = a.sources.filter((x) => x.items >= 5 && x.atOrAboveFloor / x.items < 0.3)
    if (weak.length > 0) {
      out.push({
        id: 'weak-sources',
        severity: 'watch',
        headline: `${weak.length} ${weak.length === 1 ? 'source is' : 'sources are'} contributing mostly material that does not meet the floor.`,
        evidence: weak.slice(0, 4)
          .map((x) => `${x.name} — ${num(x.atOrAboveFloor)} of ${num(x.items)} strong`).join(' · '),
        action: 'Either re-grade these sources or stop collecting from them. Volume from a source you do not trust makes the file look fuller and any argument built on it weaker, which is the worst of both.',
        votesAtStake: weak.reduce((n, x) => n + (x.items - x.atOrAboveFloor), 0),
        cellCodes: weak.slice(0, 5).map((x) => x.name),
      })
    }
  }

  // ── which files are thin ──────────────────────────────────────────────────
  const thin = a.subjects.filter((x) => x.items > 0 && x.atOrAboveFloor === 0)
  if (thin.length > 0) {
    out.push({
      id: 'thin-subjects',
      severity: 'watch',
      headline: `${thin.length} ${thin.length === 1 ? 'file has' : 'files have'} nothing in ${thin.length === 1 ? 'it' : 'them'} that meets the floor.`,
      evidence: thin.slice(0, 5).map((x) => `${x.displayName} (${num(x.items)} items)`).join(' · '),
      action: 'These files look populated and are empty for any purpose that matters. Either commission collection against them or mark them as open with nothing usable — an apparently full file that cannot be quoted is how a briefing goes wrong.',
      votesAtStake: thin.reduce((n, x) => n + x.items, 0),
      cellCodes: thin.slice(0, 6).map((x) => x.displayName),
    })
  }

  // ── what this reader cannot see ───────────────────────────────────────────
  if (s.withheldSensitive > 0) {
    out.push({
      id: 'withheld',
      severity: 'watch',
      headline: `${num(s.withheldSensitive)} items are excluded from every figure on this page.`,
      evidence: 'They are marked sensitive and your authorities do not cover them.',
      action: 'Read every total here as a floor rather than a measurement. If a decision turns on completeness, it needs somebody with the sensitive-read authority to look at the same window.',
      votesAtStake: 0,
    })
  }

  if (out.length === 0) {
    out.push({
      id: 'quiet',
      severity: 'good',
      headline: 'Nothing about this record crosses a threshold.',
      evidence: `${num(s.items)} items across ${num(s.sources)} sources and ${num(s.subjects)} files.`,
      action: 'No action. Widen the window if you are checking whether the record has been degrading.',
      votesAtStake: 0,
    })
  }

  return rank(out)
}

const ROUTES: { id: string; words: string[] }[] = [
  { id: 'standing', words: ['trust', 'strong', 'solid', 'quality', 'use this', 'stand behind', 'publish', 'good enough', 'reliable'] },
  { id: 'single-source', words: ['single source', 'one source', 'corrobor', 'confirm', 'second source', 'unverified'] },
  { id: 'contested', words: ['disput', 'debunk', 'false', 'wrong', 'contested', 'challenge'] },
  { id: 'latency', words: ['late', 'slow', 'stale', 'old', 'how fast', 'delay', 'timely', 'early warning'] },
  { id: 'undated', words: ['undated', 'no date', 'when', 'chronolog', 'timeline', 'sequence'] },
  { id: 'source-concentration', words: ['source', 'outlet', 'who told', 'depend', 'concentrat', 'media'] },
  { id: 'weak-sources', words: ['weak', 'bad source', 'grade', 'grading', 'drop', 'stop collecting'] },
  { id: 'thin-subjects', words: ['thin', 'empty', 'file', 'subject', 'gap', 'missing', 'collect more'] },
  { id: 'withheld', words: ['withheld', 'sensitive', 'hidden', 'redact', 'cannot see', 'complete'] },
]

export function ask(question: string, findings: Finding[]) {
  return route(question, findings, ROUTES)
}

/**
 * The record, written out for the model.
 *
 * The three refusals above are stated as instructions, because a model handed these counts
 * will otherwise make all three mistakes: treat unverified as doubtful, treat undated as
 * immediate, and treat a single source as unreliable.
 */
export function assayContext(a: Assay, findings: Finding[]): string {
  const s = a.standing
  const num = (n: number) => Math.round(n).toLocaleString()

  return [
    "You are advising a Kenyan campaign's research desk on what its intelligence file can",
    'support. Answer in plain language for a campaign manager or a lawyer, not an analyst.',
    'Three rules you must not break:',
    '(1) UNVERIFIED means nobody has corroborated it yet — it does NOT mean the item is false.',
    '(2) UNDATED means no event date was recorded — it does NOT mean the item arrived instantly,',
    'and you may not treat those items as having zero delay.',
    '(3) A SINGLE-SOURCE item is an unrepeated observation, not a discredited one. The response',
    'is to seek a second source, never to discard the first.',
    'Do not name or accuse any individual; describe what the record can and cannot support.',
    '',
    `WINDOW: last ${a.windowDays} days. Quality floor: ${a.floor.reliability}${a.floor.credibility}`
      + ' on the Admiralty scale (source reliability A–F, information credibility 1–6; an item'
      + ' meets the floor only if it clears BOTH).',
    `RECORD: ${num(s.items)} items across ${num(s.sources)} sources and ${num(s.subjects)} files.`
      + ` ${num(s.atOrAboveFloor)} meet the floor, ${num(s.belowFloor)} fall short.`,
    `CORROBORATION: ${num(s.corroborated)} corroborated, ${num(s.singleSource)} single-source,`
      + ` ${num(s.unverified)} unverified. ${num(s.singleSourceSubjects)} files rest entirely on one publication.`,
    `CONTESTED: ${num(s.disputed)} disputed, ${num(s.debunked)} debunked, ${num(s.redacted)} redacted.`,
    `TIMELINESS: median ${num(s.latencyP50Minutes)} minutes from event to custody, p90`
      + ` ${num(s.latencyP90Minutes)} minutes. ${num(s.undated)} items carry no event date.`,
    s.withheldSensitive > 0
      ? `WITHHELD: ${num(s.withheldSensitive)} sensitive items are excluded from every figure above.`
      : '',
    '',
    'SOURCES (name, items, of which meeting the floor, contested):',
    ...a.sources.slice(0, 10).map((x) => `- ${x.name} [${x.kindCode}, graded ${x.reliability}]:`
      + ` ${x.items}, ${x.atOrAboveFloor} strong, ${x.contested} contested`),
    '',
    'FILES (name, items, of which meeting the floor, distinct sources):',
    ...a.subjects.slice(0, 10).map((x) => `- ${x.displayName}: ${x.items}, ${x.atOrAboveFloor} strong,`
      + ` ${x.sources} sources`),
    '',
    `CATEGORIES: ${a.categories.slice(0, 8).map((c) => `${c.label} ${c.items} (${c.atOrAboveFloor} strong)`).join(', ')}`,
    `DELAY PROFILE: ${a.latency.map((b) => `${b.label} ${b.items}`).join(', ')}`,
    '',
    'WHAT OUR OWN RULES ALREADY FLAGGED (agree, refine or disagree):',
    ...findings.map((f) => `- [${f.severity}] ${f.headline} ${f.evidence}`),
  ].filter(Boolean).join('\n')
}
